import {
  CheckBoxOutlined,
  ExploreOutlined,
  FileUploadOutlined,
  FolderOutlined,
  ForestOutlined,
  HelpOutlined,
  HomeOutlined,
  HourglassEmptyOutlined,
  ImageOutlined,
  InfoOutlined,
  LoginOutlined,
  MenuBookOutlined,
  MessageOutlined,
  NotificationsActiveOutlined,
  PersonAddOutlined,
  SatelliteAltOutlined,
  SecurityOutlined,
  Unsubscribe,
  VpnKeyOutlined,
  WorkOutlined,
} from "@mui/icons-material";
import { Navigate } from "react-router-dom";
import lazyPage from "utils/lazyPage";

import LegacyThresholdingRedirect from "utils/LegacyThresholdingRedirect";

// Each page is its own chunk, so a visit downloads the page it opens rather
// than every page (and every map, raster and PDF library) in the app.
// WebRoute's Suspense shows the route loader while a chunk arrives.
const AboutUsPage = lazyPage(() => import("pages/aboutus"));
const GdacsAlertsPage = lazyPage(() => import("pages/gdacs-alerts"));
const GuidePage = lazyPage(() => import("pages/guide"));
const SatelliteStatusPage = lazyPage(() => import("pages/satellite-status"));
const AccountManagementPage = lazyPage(
  () => import("pages/account-management"),
);
const AdminPage = lazyPage(() => import("pages/admin"));
const MonitorJobPage = lazyPage(() => import("pages/admin/monitor-job"));
const EcoIntegrityAdminPage = lazyPage(
  () => import("pages/admin/eco-integrity"),
);
const Error404Page = lazyPage(() => import("pages/error"));
const FAQPage = lazyPage(() => import("pages/faq"));
const HomePage = lazyPage(() => import("pages/home"));
const Login = lazyPage(() => import("pages/login"));
const PasswordResetRequest = lazyPage(
  () => import("pages/password-reset-request"),
);
const PasswordUpdate = lazyPage(() => import("pages/password-update"));
const PendingActivationPage = lazyPage(
  () => import("pages/pending-activation"),
);
const ProductsPage = lazyPage(() => import("pages/products"));
const EventProductPage = lazyPage(() => import("pages/products/event"));
const RequestPage = lazyPage(() => import("pages/request"));
const SarfinderPage = lazyPage(() => import("pages/sarfinder"));
const EcoIntegrityPage = lazyPage(() => import("pages/eco-integrity"));
const EcoIntegrityAnalyzePage = lazyPage(
  () => import("pages/eco-integrity/analyze"),
);
const DisasterResponsePageRoute = lazyPage(
  () => import("pages/disaster-response-uploads"),
);
const SignUp = lazyPage(() => import("pages/signup"));
const MyMapsPage = lazyPage(() => import("pages/my-maps"));
const MapThresholdingPage = lazyPage(() => import("pages/my-maps/event_name"));
const ProductStudioPage = lazyPage(
  () => import("pages/my-maps/event_name/products"),
);
const UnsubscribePage = lazyPage(() => import("pages/unsubscribe"));
const EventFilesPage = lazyPage(() => import("pages/legacy-products"));
const LinkedInCaptionPage = lazyPage(() => import("pages/upload"));
const VerifyPostPage = lazyPage(() => import("pages/verify-post"));
const ProjectPage = lazyPage(() => import("pages/projects"));
const ProjectsListPage = lazyPage(() => import("pages/projects-list"));
const SharePage = lazyPage(() => import("pages/share"));

/**
 * User status types
 * @enum {string}
 */
export const USER_STATUS = {
  ACTIVATED: "activated",
  APPROVED: "approved", // approved by an admin, activation link not yet used
  PENDING_APPROVAL: "pending_approval",
  REJECTED: "rejected",
};

/**
 * User roles/groups
 * @enum {string}
 */
export const USER_ROLE = {
  SUPERUSER: "superuser", // Has access to everything
  STAFF: "staff", // Staff members
  STANDARD: "standard", // Standard users
};

/**
 * Access permission levels
 * @enum {string}
 */
export const ACCESS = {
  PUBLIC: "public", // Accessible to everyone, even not logged in
  AUTHENTICATED: "authenticated", // Any authenticated user
  UNAUTHENTICATED: "unauthenticated", // Only non-authenticated users
  ADMIN: "admin", // Admin access (superuser or staff)
  PENDING: "pending", // For pending approval users
  APPROVED: "approved", // Only for approved users
  ECO_INTEGRITY: "eco_integrity", // ecointegrity_tester group or admin
};

/**
 * Route types
 * @enum {string}
 */
export const ROUTE_TYPE = {
  STATIC: "static",
  DYNAMIC: "dynamic",
};

/**
 * Get user roles from user object
 * @param {Object} user - The user object
 * @returns {Array} - Array of roles the user has
 */
export function getUserRoles(user) {
  if (!user) return [];

  const roles = [...user.groups]; // Start with explicit groups

  // Add implicit roles based on user properties
  if (user.is_superuser) roles.push(USER_ROLE.SUPERUSER);
  if (user.is_staff) roles.push(USER_ROLE.STAFF);
  // Always add standard user role if authenticated
  if (user.id) roles.push(USER_ROLE.STANDARD);

  return roles;
}

/**
 * Application routes configuration
 * @type {Array<Object>}
 */
const RoutePaths = [
  // Public routes
  {
    type: ROUTE_TYPE.STATIC,
    name: "Home Page",
    key: "home",
    access: ACCESS.PUBLIC,
    route: "/",
    component: <HomePage />,
    icon: <HomeOutlined fontSize="small" />,
    showInMenu: true,
    order: 10,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "About Us",
    key: "aboutus",
    access: ACCESS.PUBLIC,
    route: "/about-us",
    component: <AboutUsPage />,
    icon: <InfoOutlined fontSize="small" />,
    showInMenu: true,
    order: 20,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "FAQ",
    key: "faq",
    access: ACCESS.PUBLIC,
    route: "/faq",
    component: <FAQPage />,
    icon: <HelpOutlined fontSize="small" />,
    showInMenu: true,
    order: 30,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Guide",
    key: "guide",
    access: ACCESS.PUBLIC,
    route: "/guide",
    component: <GuidePage />,
    icon: <MenuBookOutlined fontSize="small" />,
    showInMenu: true,
    order: 35,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Products",
    key: "products",
    access: ACCESS.PUBLIC,
    route: "/products",
    component: <ProductsPage />,
    icon: <WorkOutlined fontSize="small" />,
    showInMenu: true,
    order: 40,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Legacy Products",
    key: "legacy-products",
    access: ACCESS.PUBLIC,
    route: "/legacy-products",
    component: <EventFilesPage />,
    icon: <FolderOutlined fontSize="small" />,
    showInMenu: true,
    order: 45,
  },

  // Authentication routes
  {
    type: ROUTE_TYPE.STATIC,
    name: "Sign Up",
    key: "signup",
    access: ACCESS.UNAUTHENTICATED,
    route: "/signup",
    component: <SignUp />,
    icon: <PersonAddOutlined fontSize="small" />,
    showInMenu: true,
    order: 900,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Login",
    key: "login",
    access: ACCESS.UNAUTHENTICATED,
    route: "/login",
    component: <Login />,
    icon: <LoginOutlined fontSize="small" />,
    showInMenu: true,
    order: 910,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Password Reset Request",
    key: "password_reset",
    access: ACCESS.UNAUTHENTICATED,
    route: "/password_reset",
    component: <PasswordResetRequest />,
    icon: <VpnKeyOutlined fontSize="small" />,
    showInMenu: false,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Unsubscribe Event Notification",
    key: "unsubscribe",
    access: ACCESS.PUBLIC,
    route: "/unsubscribe",
    component: <UnsubscribePage />,
    icon: <Unsubscribe fontSize="small" />,
    showInMenu: false,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Pending Activation",
    key: "pending_activation",
    access: ACCESS.PENDING, // Using the existing PENDING access type
    route: "/pending-activation",
    component: <PendingActivationPage />,
    icon: <HourglassEmptyOutlined fontSize="small" />,
    showInMenu: false, // Not shown in menu, will be redirected here
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Update Password",
    key: "password_update",
    access: ACCESS.UNAUTHENTICATED,
    route: "/password_update",
    component: <PasswordUpdate />,
    showInMenu: false,
  },

  // Authenticated user routes (require approval)
  // {
  //   type: ROUTE_TYPE.STATIC,
  //   name: "Product Access",
  //   key: "productaccess",
  //   access: ACCESS.APPROVED,
  //   route: "/product-access",
  //   icon: <CheckBoxOutlined fontSize="small" />,
  //   showInMenu: true,
  //   order: 50,
  // },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Verify Post",
    key: "verifypost",
    access: ACCESS.ADMIN,
    route: "/verify-post",
    component: <VerifyPostPage />,
    icon: <CheckBoxOutlined fontSize="small" />,
    showInMenu: true,
    order: 70,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "My Maps",
    key: "mymaps",
    access: ACCESS.APPROVED,
    route: "/my-maps",
    component: <MyMapsPage />,
    icon: <ImageOutlined fontSize="small" />,
    showInMenu: true,
    order: 80,
  },

  /* The page was called "Thresholding" until it started carrying the caller's
     own in-flight requests alongside the finished bucket. Thresholding is what
     you do to one map on the page below this one — it was never the name of the
     place your products live, and a responder asking "did my product come out?"
     would not have guessed it. These two keep the old links working: an email,
     a bookmark or a chat message from before the rename still lands. */
  {
    type: ROUTE_TYPE.STATIC,
    name: "My Maps",
    key: "greyscale",
    access: ACCESS.APPROVED,
    route: "/thresholding",
    component: <Navigate to="/my-maps" replace />,
    showInMenu: false,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Account Management",
    key: "account",
    access: ACCESS.APPROVED,
    route: "/account-management",
    component: <AccountManagementPage />,
    icon: <ImageOutlined fontSize="small" />,
    showInMenu: false,
    order: 80,
  },

  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Map Thresholding",
    key: "mapthresholding",
    access: ACCESS.APPROVED,
    route: "/my-maps/:event_name",
    component: <MapThresholdingPage />,
    showInMenu: false,
  },

  /* Staff make a map's released files here (GeoTIFF, KMZ, caption, the
     MAIN and NOCAP PNGs) instead of in QGIS. */
  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Product Studio",
    key: "productstudio",
    access: ACCESS.ADMIN,
    route: "/my-maps/:event_name/products",
    component: <ProductStudioPage />,
    showInMenu: false,
  },

  /* Pre-rename deep links, including the ones in completion emails. */
  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Map Thresholding",
    key: "greyscalethresholding",
    access: ACCESS.APPROVED,
    route: "/thresholding/:event_name",
    component: <LegacyThresholdingRedirect />,
    showInMenu: false,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Request",
    key: "request",
    access: ACCESS.APPROVED,
    route: "/request",
    component: <RequestPage />,
    icon: <MessageOutlined fontSize="small" />,
    showInMenu: true,
    order: 100,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Disaster Alerts",
    key: "gdacs-alerts",
    access: ACCESS.ADMIN,
    route: "/gdacs-alerts",
    component: <GdacsAlertsPage />,
    icon: <NotificationsActiveOutlined fontSize="small" />,
    showInMenu: true,
    order: 110,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Satellite Status",
    key: "satellite-status",
    access: ACCESS.ADMIN,
    route: "/satellite-status",
    component: <SatelliteStatusPage />,
    icon: <SatelliteAltOutlined fontSize="small" />,
    showInMenu: true,
    order: 111,
  },

  // Admin-only routes
  {
    type: ROUTE_TYPE.STATIC,
    name: "Admin Panel",
    key: "admin",
    access: ACCESS.ADMIN,
    route: "/admin",
    component: <AdminPage />,
    icon: <SecurityOutlined fontSize="small" />,
    showInMenu: true,
    order: 999,
  },

  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Monitor Job",
    key: "monitor-job",
    access: ACCESS.ADMIN,
    route: "/admin/monitor-job/:jobId",
    component: <MonitorJobPage />,
    icon: <SecurityOutlined fontSize="small" />,
    showInMenu: false,
    order: 999,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Eco Integrity Admin",
    key: "eco-integrity-admin",
    access: ACCESS.ADMIN,
    route: "/admin/eco-integrity",
    component: <EcoIntegrityAdminPage />,
    icon: <ForestOutlined fontSize="small" />,
    showInMenu: true,
    order: 996,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Sarfinder",
    key: "sarfinder",
    access: ACCESS.ADMIN,
    route: "/sarfinder",
    component: <SarfinderPage />,
    icon: <ExploreOutlined fontSize="small" />,
    showInMenu: true,
    order: 90,
    roles: [USER_ROLE.GOVERNMENT, USER_ROLE.NGO, USER_ROLE.ACADEMIC],
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Eco Integrity",
    key: "eco-integrity",
    access: ACCESS.ECO_INTEGRITY,
    route: "/eco-integrity",
    component: <EcoIntegrityPage />,
    icon: <ForestOutlined fontSize="small" />,
    showInMenu: true,
    order: 95,
  },

  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Eco Integrity Project",
    key: "eco-integrity-project",
    access: ACCESS.ECO_INTEGRITY,
    route: "/eco-integrity/projects/:projectId",
    component: <EcoIntegrityAnalyzePage />,
    showInMenu: false,
  },

  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Eco Integrity Explore",
    key: "eco-integrity-explore",
    access: ACCESS.ECO_INTEGRITY,
    route: "/eco-integrity/explore/:entryId",
    component: <EcoIntegrityAnalyzePage />,
    showInMenu: false,
  },

  {
    type: ROUTE_TYPE.STATIC,
    name: "Response Uploads",
    key: "disaster-response-uploads",
    access: ACCESS.ADMIN,
    route: "/disaster-response-uploads",
    component: <DisasterResponsePageRoute />,
    icon: <SatelliteAltOutlined fontSize="small" />,
    showInMenu: false,
  },

  // Dynamic routes
  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Event Product Page",
    key: "eventproduct",
    access: ACCESS.PUBLIC,
    route: "/event/:event_name",
    component: <EventProductPage />,
    showInMenu: false,
  },
  {
    type: ROUTE_TYPE.STATIC,
    name: "Upload",
    key: "upload",
    access: ACCESS.ADMIN,
    route: "/upload",
    component: <LinkedInCaptionPage />,
    icon: <FileUploadOutlined fontSize="small" />,
    showInMenu: true,
    order: 60,
  },

  /* Shared projects: a response's data, opened by a share link and its
     password, or by members and staff. Public routes because the backend
     decides access on every request (projects/access.py); the pages only ask. */
  {
    // The ones an account opens without a link. In the header's tools for
    // staff, and for members once they belong to one (user.has_projects).
    type: ROUTE_TYPE.STATIC,
    name: "Projects",
    key: "projects",
    access: ACCESS.APPROVED,
    route: "/projects",
    component: <ProjectsListPage />,
    showInMenu: false,
  },
  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Shared link",
    key: "projectshare",
    access: ACCESS.PUBLIC,
    route: "/share/:token",
    component: <SharePage />,
    showInMenu: false,
  },
  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Shared project",
    key: "project",
    access: ACCESS.PUBLIC,
    route: "/p/:slug",
    component: <ProjectPage />,
    showInMenu: false,
  },
  {
    type: ROUTE_TYPE.DYNAMIC,
    name: "Shared project site",
    key: "projectsite",
    access: ACCESS.PUBLIC,
    route: "/p/:slug/:site",
    component: <ProjectPage />,
    showInMenu: false,
  },

  // Error route - always keep this at the end as a fallback
  {
    type: ROUTE_TYPE.STATIC,
    name: "Error 404",
    key: "error404",
    access: ACCESS.PUBLIC,
    route: "*",
    componenp: <Error404Page />,
    showInMenu: false,
  },
];

/**
 * Check if user is approved
 * @param {Object} user - The user object
 * @returns {boolean} - True if the user is approved
 */
export function isUserApproved(user) {
  if (!user) return false;
  return user.status === USER_STATUS.ACTIVATED && user.terms_accepted;
}

/**
 * Check if user is an admin
 * @param {Object} user - The user object
 * @returns {boolean} - True if the user is an admin
 */
export function isUserAdmin(user) {
  if (!user) return false;
  return user.is_superuser || user.is_staff;
}

/**
 * Check if user can see private greyscale products.
 *
 * Mirrors the backend predicate (see products/views.py and
 * bucket.permissions.CanAccessGreyscaleData): staff, superusers, the
 * greyscale_access group, or the access_greyscale_data permission — which
 * /accounts/me/ returns by codename, group-inherited ones included.
 *
 * @param {Object|null} user - The user object
 * @returns {boolean} - True if the user can access greyscale products
 */
export function hasGreyscaleAccess(user) {
  if (!user) return false;
  if (isUserAdmin(user)) return true;
  if (Array.isArray(user.groups) && user.groups.includes("greyscale_access"))
    return true;
  return (
    Array.isArray(user.permissions) &&
    user.permissions.includes("access_greyscale_data")
  );
}

export function isEcoIntegrityTester(user) {
  if (!user) return false;
  if (isUserAdmin(user)) return true;
  return (
    Array.isArray(user.groups) && user.groups.includes("ecointegrity_tester")
  );
}

/**
 * Check if a user can access a route
 * @param {Object|null} user - The user object or null if not authenticated
 * @param {string} routeKey - The route key
 * @returns {boolean} - True if the user has access
 */
export function canUserAccessRoute(user, routeKey) {
  const route = RoutePaths.find((r) => r.key === routeKey);
  if (!route) return false;

  // Handle public routes
  if (route.access === ACCESS.PUBLIC) return true;

  // Handle unauthenticated routes
  if (route.access === ACCESS.UNAUTHENTICATED) return !user?.username;

  // Handle pending routes
  if (route.access === ACCESS.PENDING) {
    return (
      !!user &&
      (user.status === USER_STATUS.PENDING_APPROVAL ||
        user.status === USER_STATUS.APPROVED)
    );
  }

  if (!isUserApproved(user)) return false;

  // Handle authenticated routes
  if (route.access === ACCESS.AUTHENTICATED) return !!user;

  // Handle admin routes
  if (route.access === ACCESS.ADMIN) return isUserAdmin(user);

  // Handle eco-integrity tester routes
  if (route.access === ACCESS.ECO_INTEGRITY) return isEcoIntegrityTester(user);

  // Handle approved routes
  if (route.access === ACCESS.APPROVED) {
    if (!isUserApproved(user)) return false;

    // Check for specific roles if defined
    if (route.roles && route.roles.length > 0) {
      const userRoles = getUserRoles(user);
      return (
        route.roles.some((role) => userRoles.includes(role)) ||
        isUserAdmin(user)
      );
    }

    return true;
  }

  return false;
}

/**
 * Get route path by key
 * @param {string} key - The route key
 * @param {Object} [params] - URL parameters to include
 * @returns {string} - The route path
 */
export const getRoute = (key, params) => {
  const route = RoutePaths.find((element) => element.key === key);
  if (!route) {
    console.warn(`Route with key "${key}" not found.`);
    return "/";
  }

  // Add querystring if params are provided
  if (params) {
    const queryString = new URLSearchParams(params).toString();
    return route.route + (queryString ? `?${queryString}` : "");
  }

  return route.route;
};

/**
 * Get dynamic route with parameters
 * @param {string} key - The route key
 * @param {string|Object} data - Parameter value or object with parameter values
 * @returns {string|null} - The populated route or null if not found
 */
export const getDynamicRoute = (key, data) => {
  const route = RoutePaths.find(
    (element) => element.key === key && element.type === ROUTE_TYPE.DYNAMIC,
  );

  if (!route) return null;

  if (typeof data === "object") {
    // Handle multiple parameters in the route
    let populatedRoute = route.route;

    Object.entries(data).forEach(([param, value]) => {
      populatedRoute = populatedRoute.replace(`:${param}`, value);
    });

    return populatedRoute;
  }

  // Handle simple parameter replacement
  return route.route.replace(/:\w+/, data);
};

/**
 * Get menu items based on user's access rights
 * @param {Object|null} user - The user object or null if not authenticated
 * @returns {Array} - Filtered and sorted menu items
 */
export function getMenu(user) {
  return RoutePaths.filter((route) => {
    // Skip items not meant for the menu
    if (!route.showInMenu) return false;

    // Check if user can access this route
    return canUserAccessRoute(user, route.key);
  })
    .sort((a, b) => (a.order || 999) - (b.order || 999))
    .map((route) => ({
      title: route.name,
      key: route.key,
      icon: route.icon,
      route: route.route,
    }));
}

export default RoutePaths;
